import * as angular from 'angular';

import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Logger } from '@mode/shared/contract-common';
import { getMetaContent } from '@mode/shared/util-js';
import { INITIATOR_CONTEXT, serializeAndStripIncludes, UriTemplateService } from '@mode/shared/util-halogen';
import { ModelCache } from '../model-cache/model-cache';
import { UrlExpanderService } from '../url-expander/url-expander.service';
import { ModeResourceFactoryService } from './mode-resource.factory';

export class ModeEntryPoint {
  path: string;
  cache: ModelCache.Cache;

  constructor(
    name: string,
    path: string,
    private $http: HttpClient,
    private urlExpander: UrlExpanderService,
    private modeResourceFactory: ModeResourceFactoryService,
    modelCache: ModelCache,
    private $log: Logger,
    $window: Window,
    private uriTemplateService: UriTemplateService
  ) {
    this.cache = modelCache.getOrCreate('mode.entry');
    this.path = path || getMetaContent($window.document, `${name}-entry-path`);
  }

  $enter(params) {
    const passedParams = angular.copy(params);
    const includes = serializeAndStripIncludes(passedParams);
    const cacheId = JSON.stringify(passedParams) + JSON.stringify(includes);

    if (angular.isDefined(this.cache.get(cacheId))) {
      return this.cache.get(cacheId);
    }

    if (!angular.isDefined(this.path)) {
      return Promise.reject(new Error('Not found'));
    }
    const deferred = new Promise((resolve, reject) => {
      const basePath = this.urlExpander.expand(this.path);
      const template = this.uriTemplateService.parse(basePath);

      this.$log.debug('Entering API at', basePath, passedParams);

      this.$http
        .get(template.expand(passedParams), {
          params: includes,
          context: new HttpContext().set(INITIATOR_CONTEXT, new Error()),
        })
        .toPromise()
        .then(
          (body) => {
            resolve(this.modeResourceFactory.create(body));
          },
          (errorResponse) => {
            if (errorResponse instanceof HttpErrorResponse) {
              reject(new Error(errorResponse.message));
            } else {
              reject(errorResponse);
            }
          }
        );
    });
    return this.cache.put(cacheId, deferred);
  }
}

@Injectable({ providedIn: 'root' })
export class ModeEntryPointFactoryService {
  constructor(
    private $http: HttpClient,
    private urlExpander: UrlExpanderService,
    private modeResourceFactory: ModeResourceFactoryService,
    private modelCache: ModelCache,
    private $log: Logger,
    private $window: Window,
    private uriTemplateService: UriTemplateService
  ) {}

  create(name, path?) {
    return new ModeEntryPoint(
      name,
      path,
      this.$http,
      this.urlExpander,
      this.modeResourceFactory,
      this.modelCache,
      this.$log,
      this.$window,
      this.uriTemplateService
    );
  }
}
